/* RESPONSIBLE TEAM: team-knowledge-foundations */
import { type NodeSpec } from 'prosemirror-model';
import type ComposerConfig from '@intercom/composer-ember/lib/config/composer-config';

type CustomBlockHandler = NonNullable<ComposerConfig['customBlockHandlers']>[number];
type CustomInlineHandler = NonNullable<ComposerConfig['customInlineHandlers']>[number];

interface DynamicContentReferenceBlock {
  type: string;
  dynamicContentKey?: string;
  block_id?: string;
}

const dynamicContentReferenceSpec: NodeSpec = {
  inline: false,
  attrs: {
    type: { default: 'dynamicContentReference' },
    dynamicContentKey: { default: '' },
    block_id: { default: '' },
  },
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  parseDOM: [
    {
      tag: 'div[data-dynamic-content-reference]',
      getAttrs(dom: HTMLElement) {
        return {
          dynamicContentKey: dom.getAttribute('data-dynamic-content-key') ?? '',
          block_id: dom.getAttribute('data-block-id') ?? '',
        };
      },
    },
  ],
  toDOM(node) {
    return [
      'div',
      {
        class:
          'dynamic-content-reference flex items-center gap-2 py-2 px-3 my-1 border border-neutral-border rounded-md bg-neutral-container cursor-pointer select-none',
        'data-dynamic-content-reference': '',
        'data-dynamic-content-key': node.attrs.dynamicContentKey,
        'data-block-id': node.attrs.block_id,
      },
      [
        'span',
        {
          class:
            'inline-flex items-center justify-center w-5 h-5 rounded bg-accent-container text-accent text-xs font-semibold flex-shrink-0',
        },
        'DC',
      ],
      ['span', { class: 'text-sm text-default' }, String(node.attrs.dynamicContentKey)],
    ];
  },
};

export const dynamicContentReferenceHandler: CustomBlockHandler = {
  type: 'dynamicContentReference',
  spec: dynamicContentReferenceSpec,
  parse(block, parser) {
    let dcBlock = block as DynamicContentReferenceBlock;
    let spec = parser.schema.nodes.dynamicContentReference;
    if (spec) {
      parser.documentBuilder.openNode(spec, {
        type: dcBlock.type,
        dynamicContentKey: dcBlock.dynamicContentKey ?? '',
        block_id: dcBlock.block_id ?? '',
      });
      parser.documentBuilder.closeNode();
    }
  },
  serialize(node) {
    return {
      type: node.attrs.type as string,
      dynamicContentKey: node.attrs.dynamicContentKey as string,
      block_id: node.attrs.block_id as string,
    };
  },
};

const dynamicContentTemplateSpec: NodeSpec = {
  inline: true,
  attrs: {
    dynamicContentKey: { default: '' },
  },
  group: 'inline',
  atom: true,
  selectable: true,
  parseDOM: [
    {
      tag: 'span.dynamic-content-template',
      getAttrs(dom: HTMLSpanElement) {
        return {
          dynamicContentKey: dom.getAttribute('data-dynamic-content-key') ?? '',
        };
      },
    },
  ],
  toDOM(node) {
    return [
      'span',
      {
        class:
          'dynamic-content-template inline-block py-px px-1.5 rounded bg-accent-container text-accent text-xs font-medium',
        'data-dynamic-content-key': node.attrs.dynamicContentKey,
      },
      `{{${node.attrs.dynamicContentKey}}}`,
    ];
  },
};

export const dynamicContentTemplateHandler: CustomInlineHandler = {
  name: 'dynamicContentTemplate',
  spec: dynamicContentTemplateSpec,
  detect(astNode) {
    return !!(
      astNode.value === 'span' &&
      astNode.attributes?.some(
        (attr) => attr.name === 'class' && (attr.value ?? '').includes('dynamic-content-template'),
      )
    );
  },
  parse(astNode, parser) {
    let attrs = parser.collectAttrs(astNode);
    let nodeType = parser.schema.nodes.dynamicContentTemplate;
    if (!nodeType) {
      return;
    }
    parser.documentBuilder.openNode(nodeType, {
      dynamicContentKey: attrs['data-dynamic-content-key'] ?? '',
    });
    parser.documentBuilder.closeNode();
  },
  serialize(node, serializer) {
    let key = serializer.escape(node.attrs.dynamicContentKey as string);
    return `<span class="dynamic-content-template" data-dynamic-content-key="${key}">{{${key}}}</span>`;
  },
};
